/* Photo editor — rotate / straighten / crop
 *
 * Moved verbatim from currencykeep_2026-08-03.html, lines 638-733.
 * The body below is byte-identical to the original; only the import
 * block above and the export block below were added. (Phase 1, Step 2.)
 */

import { CORNER_FILL, MIN_CROP_SIDE, bbox, validateTransform } from '../shared/photo-transform.js'
import { putBrand } from './community.js'
import { putPhoto } from './photos.js'
import { render } from './render.js'
import { $, state, toast } from './state.js'
import { uploadSlot } from './migrate.js'
import { applyReading } from './suggest.js'
import { MATCHED_TOAST } from './custom.js'
import { xhrUpload } from './photo-xhr.js'

/* ---------- photo editor (rotate / straighten / crop) ---------- */
/* ⚠ `file` IS NEW IN PHASE 4 STEP 6 AND IT IS THE POINT OF THE STEP. The
 * editor has always decoded the upload into an `Image` and thrown the
 * bytes away; the note of record was whatever the canvas re-encoded. The
 * File is now held so the ORIGINAL can be stored beside the preview and
 * the encoder can do the rotate and crop at full resolution later. */
let ED={img:null,file:null,angle:0,base:0,fine:0,crop:{x:0,y:0,w:1,h:1},cw:0,ch:0,target:null};
function openEditor(target){if((target==="logo"||target==="banner")&&!state.username){toast("Set a username in your Vault first");return;}ED.target=target;const f=$("edFile");f.value="";f.click();}
/* ── ⚠⚠ THE DECODE CAN FAIL, AND UNTIL 2026-08-23 IT FAILED IN SILENCE ──
 * There was no `onerror` on either the FileReader or the Image, so a file the
 * browser cannot decode produced NOTHING: no editor, no message, no console
 * line. The collector taps Add, picks their photo, and the app does not react.
 *
 * ⚠ IT IS NOT A CORNER CASE, IT IS A WHOLE BROWSER. Measured 2026-08-23:
 * **CHROME CANNOT DECODE HEIC AT ALL** — `new Image()` fires `error` and
 * `createImageBitmap` throws `InvalidStateError`, on both of Jesse's real
 * captures, with a PNG control loading in the same run. So every collector
 * on desktop Chrome or Firefox uploading straight off an iPhone hits this,
 * and today the app says nothing at all.
 *
 * This does not make those browsers work — nothing here can. It makes the
 * failure VISIBLE, which is the same rule as *"Photo saved — original not
 * kept"* below: the app says which of two true things is true. */
/* ⚠⚠ `input` CLEARS WHICHEVER INPUT ACTUALLY FIRED, AND IT KEPT ITS
 * PLACE WHEN THE CAMERA WENT — JESSE'S RULING, 2026-09-06.
 *
 * This used to clear `#edFile` BY NAME. It was found while wiring a
 * second input, where clearing by id is wrong half the time — but the
 * defect it fixes is on the PICKER path and does not need a second
 * input to exist: `change` does not fire when the same file is chosen
 * again, so a file left sitting in an input after a failed decode makes
 * RE-PICKING THAT SAME PHOTOGRAPH DO NOTHING AT ALL. A dead control
 * with no message — the exact silent-failure shape the `onerror`
 * handlers below were added to end on 2026-08-23.
 *
 * ⚠ `ev.target` already carried the answer, so this costs nothing and
 * cannot go stale if a second input ever returns. */
function edFailed(why,input){
  console.error("editor could not open the file:",why);
  input.value="";
  toast("Couldn't open that photo — this browser can't read that format");
}
/* ══ ⚠⚠⚠ ONE HANDLER, BOUND TWICE — NOT TWO COPIES ══════════════════
 * Jesse, on ruling one camera input rather than two: *"this project has
 * spent two sessions on what a second copy of a line costs."* The same
 * reasoning applies one level down. This body was an anonymous arrow
 * attached to `#edFile`; the camera input needs identical behaviour, and
 * a second listener would be a second copy of the decode, the failure
 * handling and the auto-crop rule, free to drift.
 * ⚠ It is NAMED and REGISTERED TWICE instead. `ev.target` already
 * carried which input fired, so nothing new has to be tracked to know. */
function edChosen(ev){
  const input=ev.target;const file=input.files[0];if(!file)return;
  const rd=new FileReader();rd.onerror=()=>edFailed("FileReader failed on "+file.type,input);
  rd.onload=()=>{const im=new Image();im.onerror=()=>edFailed("the browser cannot decode "+(file.type||"this file"),input);im.onload=()=>{ED.img=im;ED.file=file;ED.base=0;ED.fine=0;ED.crop={x:0,y:0,w:1,h:1};$("edAngle").value=0;$("editor").classList.remove("hidden");edRender();if(ED.target!=="logo"&&ED.target!=="banner")autoCrop();};im.src=rd.result;};rd.readAsDataURL(file);
}
$("edFile").addEventListener("change",edChosen);
/* `bbox` used to be defined here. It is now imported from
 * `shared/photo-transform.js` — ONE definition, shared with the encoder.
 * Phase 4 Step 6, Jesse: the second copy "could slip past".
 * `photos-check` asserts this copy is GONE rather than merely agreeing. */
function edRender(){
  const im=ED.img;if(!im)return;ED.angle=ED.base+ED.fine;
  const b=bbox(im.width,im.height,ED.angle);
  // CSS display size: fill most of the frame width
  const MAXCSS=Math.min(392,(window.innerWidth||400)-32);
  const cssScale=Math.min(MAXCSS/b.w,MAXCSS/b.h);
  const dw=Math.round(b.w*cssScale),dh=Math.round(b.h*cssScale);ED.cw=dw;ED.ch=dh;
  // bitmap resolution: supersample for the screen's pixel density, capped at source & 2600px long edge
  const dpr=Math.min(window.devicePixelRatio||1,3);
  const rscale=Math.min(cssScale*dpr*1.6, 2600/Math.max(b.w,b.h), 1);
  const bw=Math.max(1,Math.round(b.w*rscale)),bh=Math.max(1,Math.round(b.h*rscale));
  const cv=$("edCanvas");cv.width=bw;cv.height=bh;cv.style.width=dw+"px";cv.style.height=dh+"px";
  const ctx=cv.getContext("2d");ctx.imageSmoothingEnabled=true;ctx.imageSmoothingQuality="high";ctx.clearRect(0,0,bw,bh);
  ctx.save();ctx.translate(bw/2,bh/2);ctx.rotate(ED.angle*Math.PI/180);
  ctx.drawImage(im,-im.width*rscale/2,-im.height*rscale/2,im.width*rscale,im.height*rscale);ctx.restore();
  placeCrop();
}
function placeCrop(){const c=ED.crop;const el=$("edCrop");el.style.left=(c.x*ED.cw)+"px";el.style.top=(c.y*ED.ch)+"px";el.style.width=(c.w*ED.cw)+"px";el.style.height=(c.h*ED.ch)+"px";}
(function(){let mode=null,sx,sy,start;
 const cvRect=()=>$("edCanvas").getBoundingClientRect();
 const pt=e=>{const t=e.touches?e.touches[0]:e;return{x:t.clientX,y:t.clientY};};
 function down(e){const tg=e.target.closest(".h");mode=tg?("r"+tg.dataset.h):"move";const p=pt(e);sx=p.x;sy=p.y;start=Object.assign({},ED.crop);e.preventDefault();}
 function move(e){if(!mode)return;const p=pt(e),r=cvRect();const dx=(p.x-sx)/r.width,dy=(p.y-sy)/r.height;const MIN=MIN_CROP_SIDE;let c;
   if(mode==="move"){c={x:Math.min(Math.max(0,start.x+dx),1-start.w),y:Math.min(Math.max(0,start.y+dy),1-start.h),w:start.w,h:start.h};}
   else{let x1=start.x,y1=start.y,x2=start.x+start.w,y2=start.y+start.h;
     if(mode.includes("l"))x1=Math.min(Math.max(0,start.x+dx),x2-MIN);
     if(mode.includes("r"))x2=Math.max(Math.min(1,x2+dx),x1+MIN);
     if(mode.includes("t"))y1=Math.min(Math.max(0,start.y+dy),y2-MIN);
     if(mode.includes("b"))y2=Math.max(Math.min(1,y2+dy),y1+MIN);
     c={x:x1,y:y1,w:x2-x1,h:y2-y1};}
   ED.crop=c;placeCrop();e.preventDefault();}
 function up(){mode=null;}
 const cr=$("edCrop");cr.addEventListener("mousedown",down);cr.addEventListener("touchstart",down,{passive:false});
 window.addEventListener("mousemove",move);window.addEventListener("touchmove",move,{passive:false});
 window.addEventListener("mouseup",up);window.addEventListener("touchend",up);
})();
document.querySelectorAll("[data-rot]").forEach(b=>b.addEventListener("click",()=>{ED.base=(ED.base+ +b.dataset.rot)%360;ED.crop={x:0,y:0,w:1,h:1};edRender();}));
$("edAngle").addEventListener("input",e=>{ED.fine=+e.target.value;edRender();});
function autoCrop(){
  const src=$("edCanvas");if(!ED.img)return;
  const DW=Math.min(300,src.width),DH=Math.max(1,Math.round(src.height*DW/src.width));
  const dc=document.createElement("canvas");dc.width=DW;dc.height=DH;const dcx=dc.getContext("2d");
  dcx.drawImage(src,0,0,DW,DH);
  let data;try{data=dcx.getImageData(0,0,DW,DH).data;}catch(e){return;}
  const s=Math.max(4,Math.round(Math.min(DW,DH)*0.06));const corners=[];
  const grab=(x0,y0)=>{for(let y=y0;y<y0+s;y++)for(let x=x0;x<x0+s;x++){const i=(y*DW+x)*4;if(data[i+3]>=128)corners.push([data[i],data[i+1],data[i+2]]);}};
  grab(0,0);grab(DW-s,0);grab(0,DH-s);grab(DW-s,DH-s);
  let bg=[22,42,32];if(corners.length)bg=[0,1,2].map(k=>Math.round(corners.reduce((a,c)=>a+c[k],0)/corners.length));
  const T=44*44;const colC=new Array(DW).fill(0),rowC=new Array(DH).fill(0);
  for(let y=0;y<DH;y++)for(let x=0;x<DW;x++){const i=(y*DW+x)*4;let fg=false;
    if(data[i+3]>=128){const dr=data[i]-bg[0],dg=data[i+1]-bg[1],db=data[i+2]-bg[2];fg=(dr*dr+dg*dg+db*db)>T;}
    if(fg){colC[x]++;rowC[y]++;}}
  const colMax=Math.max.apply(null,colC),rowMax=Math.max.apply(null,rowC);
  if(colMax<DH*0.15||rowMax<DW*0.15){toast("Couldn't find the note — crop by hand");return;}
  const ct=colMax*0.25,rt=rowMax*0.25;
  let x1=colC.findIndex(v=>v>ct),y1=rowC.findIndex(v=>v>rt),x2=DW-1,y2=DH-1;
  for(let x=DW-1;x>=0;x--){if(colC[x]>ct){x2=x;break;}}
  for(let y=DH-1;y>=0;y--){if(rowC[y]>rt){y2=y;break;}}
  if(x1<0||y1<0||x2<=x1||y2<=y1){toast("Couldn't find the note — crop by hand");return;}
  const area=((x2-x1)*(y2-y1))/(DW*DH);
  ED.crop={x:x1/DW,y:y1/DH,w:(x2-x1)/DW,h:(y2-y1)/DH};placeCrop();
  toast(area>0.94?"Note fills the frame":"Auto-cropped tight");
}
$("edAuto").addEventListener("click",autoCrop);
$("edReset").addEventListener("click",()=>{ED.crop={x:0,y:0,w:1,h:1};placeCrop();toast("Crop reset");});
$("edCancel").addEventListener("click",()=>{$("editor").classList.add("hidden");ED.img=null;});
/* ── ⚠⚠ WHAT THIS HANDLER PRODUCES CHANGED IN PHASE 4 STEP 6 ─────────
 * It used to produce ONE thing — a re-encoded JPEG data URL — and that
 * was the note of record. Three lossy operations stood between the
 * collector's file and what got stored: a 4200px rotation canvas, a
 * 3200px crop canvas, and a browser JPEG export at q0.95 stepping down
 * to 0.70, on a path where 4:4:4 is not even on offer.
 *
 * It now produces TWO things, and the canvas output is the lesser one:
 *
 *   the ORIGINAL FILE + {angle, crop}   the note of record. Untouched
 *                                       bytes; the encoder does the
 *                                       geometry at full resolution.
 *   the canvas JPEG                     a PREVIEW, for display only.
 *
 * ⚠ THE PREVIEW PIPELINE BELOW IS DELIBERATELY UNCHANGED — same 4200,
 * same 3200, same q0.95 and same step-down. Now that it is no longer the
 * note of record its size is a free parameter (the display tier is
 * 2000px, which would reclaim storage), but changing it would be trading
 * displayed quality for storage nobody asked to spend. Flagged in
 * BUILD_NOTES as Jesse's call rather than taken here.
 *
 * ⚠⚠ BRAND IMAGES TAKE THE SAME TWO-ARTIFACT SHAPE AS OF 2026-09-07 —
 * the Phase 5 client half, with Jesse present. `ED.file` is handed to
 * `putBrand` beside the preview and the transform, so the ORIGINAL is
 * what uploads and the server does the geometry, exactly as for a note.
 *
 * ⚠⚠⚠ AND FOR A LOGO THE PREVIEW IS PNG WITH NO BACKGROUND, BECAUSE THE
 * RULING IS PER SLOT (logo PNG, banner JPEG, 2026-09-06). This handler
 * bakes `CORNER_FILL` under every note and every banner — a photograph
 * over a gradient it can cover — and it must NOT do that under a logo:
 * a transparent mark on a filled canvas exported as JPEG is the
 * transparency gone before the server ever sees a byte, and it looks
 * correct on every screen, which is the defect the whole ruling exists
 * for. The original is what uploads either way; this is about the
 * preview being honest about what the row will carry. `brand-check` §6
 * watches both the fill and the export type per target. */
$("edDone").addEventListener("click",async()=>{
  const im=ED.img;if(!im||!ED.target)return;
  const isBrand=ED.target==="logo"||ED.target==="banner";const isLogo=ED.target==="logo";
  const b=bbox(im.width,im.height,ED.angle);const EXP=4200;const es=Math.min(EXP/Math.max(b.w,b.h),1);
  const ew=Math.round(b.w*es),eh=Math.round(b.h*es);
  const tmp=document.createElement("canvas");tmp.width=ew;tmp.height=eh;const tx=tmp.getContext("2d");
  tx.imageSmoothingEnabled=true;tx.imageSmoothingQuality="high";
  if(!isLogo){tx.fillStyle=CORNER_FILL;tx.fillRect(0,0,ew,eh);}
  tx.save();tx.translate(ew/2,eh/2);tx.rotate(ED.angle*Math.PI/180);tx.drawImage(im,-im.width*es/2,-im.height*es/2,im.width*es,im.height*es);tx.restore();
  const c=ED.crop,cx=Math.round(c.x*ew),cy=Math.round(c.y*eh),cwd=Math.round(c.w*ew),chd=Math.round(c.h*eh);
  const OUT=3200,os=Math.min(OUT/Math.max(cwd,chd),1),ow=Math.round(cwd*os),oh=Math.round(chd*os);
  const out=document.createElement("canvas");out.width=ow;out.height=oh;const ox=out.getContext("2d");
  ox.imageSmoothingEnabled=true;ox.imageSmoothingQuality="high";
  if(!isLogo){ox.fillStyle=CORNER_FILL;ox.fillRect(0,0,ow,oh);}
  ox.drawImage(tmp,cx,cy,cwd,chd,0,0,ow,oh);
  let url;
  if(isLogo){url=out.toDataURL("image/png");}
  else{
    url=out.toDataURL("image/jpeg",0.95);
    // each photo now has its own ~5MB slot; only step down if a single image would exceed it
    let q=0.95;while(url.length>4700000&&q>0.7){q-=0.05;url=out.toDataURL("image/jpeg",q);}
  }
  if(isBrand){
    const checked=validateTransform({angle:ED.angle,crop:ED.crop});
    if(!checked.ok)console.error("editor produced a spec the contract refuses:",checked.message);
    await putBrand(ED.target,{
      preview:url,
      original:checked.ok?ED.file:null,
      transform:checked.ok?checked.transform:null,
    });
    $("editor").classList.add("hidden");ED.img=null;ED.file=null;render();return;
  }

  /* ── THE SPEC, VALIDATED BY THE SAME MODULE THE ENCODER USES ───────
   * `ED.crop` is already in the reference frame the spec is defined in:
   * the drag handler places it over the ROTATED canvas, whose size is
   * `bbox(im.width, im.height, angle)`. So the fractions apply to the
   * full-resolution original unchanged — that is why the crop box could
   * move to a shared module without being recomputed.
   *
   * ⚠ IT IS VALIDATED HERE RATHER THAN ASSUMED VALID. The drag handler
   * clamps every edge, so a refusal means a bug in this file — and the
   * honest response to that is to store NO spec rather than one the
   * encoder will reject at upload. The preview is still saved, so the
   * collector loses nothing they can see; what they lose is the original,
   * which is the same position every pre-Step-6 photo is already in. */
  const parts=ED.target.split(":");
  const checked=validateTransform({angle:ED.angle,crop:ED.crop});
  if(!checked.ok)console.error("editor produced a spec the contract refuses:",checked.message);
  /* ⚠ HIS RULING OF 2026-10-22 — THE REF BEFORE THIS PHOTO, read HERE and
   * not later: `putPhoto` rewrites the slot's ref to `idb:` (photos.js), so
   * whether this is a REPLACEMENT (the slot carried an `r2:` ref) is only
   * knowable before it runs. It rides to the landing as `previousRef`
   * (`uploadSlot` → the verdict), where `applyReading` tests it. Data
   * only; the decision is suggest.js's. */
  const hBefore=state.collection.find(x=>x.id===parts[0]);
  const previousRef=hBefore?((parts[1]==="front"?hBefore.frontRef:hBefore.backRef)||null):null;
  const saved=await putPhoto(parts[0],parts[1],{
    preview:url,
    original:checked.ok?ED.file:null,
    spec:checked.ok?checked.transform:null,
  });
  $("editor").classList.add("hidden");ED.img=null;ED.file=null;render();
  /* ⚠ THE COLLECTOR IS TOLD WHEN THE ORIGINAL DID NOT SURVIVE. "Photo
   * saved" over a photo whose original is gone is the quiet failure this
   * whole step exists to remove; it costs one word to be honest. */
  /* ⚠⚠ THREE OUTCOMES, NOT TWO — 2026-08-25. The third is the one that
   * used to be a lie: before `putPhoto` refused to record a failed write,
   * a quota failure toasted "Photo saved" over a photograph that was
   * never stored, and the note kept a ref claiming it existed. The app
   * says which of three true things is true, on the same rule as
   * "original not kept". */
  toast(!saved.previewSaved
    ? "Photo NOT saved — storage is full"
    : saved.keptOriginal ? "Photo saved" : "Photo saved — original not kept");
  /* ══ INSTANT UPLOAD — PHASE 6.5 STEP 1, HIS RULING 2026-09-13 ════════
   * *"The photo uploads at save, not on the next boot."* Fired, not
   * awaited — the same measurement that shaped the boot sweep and the
   * brand path: an upload is ~10 s and the collector has already been
   * shown the photograph. `replace:true` supersedes whatever this slot had
   * in flight (*"a new edit on the same slot cancels the in-flight upload
   * and starts fresh"* — aborted while the bytes are moving, discarded
   * once they have arrived; `migrate.js` says why); `head:true` puts it in
   * front of a boot backlog; `after` is the `idb:` row write, waited on
   * before a byte is sent.
   *
   * ⚠ ONLY AFTER A SAVED PREVIEW. A failed save has no bytes to send, and
   * the toast above has already said so. ⚠ AND ONLY WITH A STORE — with
   * no backend the app runs in memory and there is nothing to read.
   *
   * ⚠ `cancelled` and `in_flight` are the collector's own doing and are
   * not spoken about. Anything else keeps the `idb:` ref, brings the badge
   * back, and says the sweep's sentence once — it is exactly true, and
   * the next boot retries. */
  if(saved.previewSaved&&window.storage){
    uploadSlot({
      storage:window.storage,collection:state.collection,holdingId:parts[0],slot:parts[1],
      replace:true,head:true,after:saved.saved,fetchImpl:xhrUpload,previousRef,
      /* ⚠⚠ STEP 4.5b — THE ARRIVAL. `onChange` is the ONLY seam that
       * already means what a suggestion needs: it fires only when the
       * upload SUCCEEDED and the landing is the CURRENT generation, so
       * a stale landing — a reading of a photograph the collector has
       * already replaced — never reaches it. ⚠ `applyReading` writes
       * nothing to the holding; it puts entries on `state.pending`,
       * which is memory-only by his ruling 5. */
      /* ⚠ STEP 7.3(c): a confident match TOASTS HERE and nowhere else —
       * the instant path is the one where the collector was on the screen
       * when the photo went up, and it still checks they are, because an
       * upload takes seconds. The boot sweep never toasts. Same text as
       * the typed conversion, one constant. */
      onChange:(v)=>{const r=applyReading(state,v);render();
        if(r&&r.identity&&r.identity.converted&&state.screen==="holding"&&state.sel===v.holdingId)toast(MATCHED_TOAST(r.identity.converted.fr));},
    }).then(v=>{
      /* ══ ⚠⚠ THE SUCCESS LINE — HIS RULING, 2026-09-13, AFTER THE WALK ══
       *
       * *"Build the success log — one line on the instant path, ms and
       * bytes to the console. It costs nothing and turns 'under 5 seconds'
       * into a number the next walk can read."*
       *
       * ⚠ WHY IT EXISTS AT ALL: `migrateOne` has always computed `ms`,
       * `sentBytes`, `sentType` and `sourceKind`, and NOTHING printed them
       * on a success — only the failure warn below. So the walk of
       * 2026-09-13 could only report *"under 5 seconds"*, by eye, because
       * on a phone there was no number to read. **This is the whole
       * instrument for the upload's duration on a real device.**
       *
       * ⚠⚠ A STALE VERDICT SAYS SO. A superseded upload that ran to an
       * answer is `ok` — it stored tiers — but its answer was DISCARDED
       * and the collector's photograph is the next one. Printing it as a
       * plain success would make two lines look like two photographs.
       *
       * ⚠ WHAT THIS LINE DOES NOT MEASURE, so nobody reads it as the
       * pair: **the local hash's cost.** That is computed in `putPhoto`
       * (`photos.js`) BEFORE this path is entered, and is not in the
       * verdict — it needs its own line, which is not built and is his
       * call. ⚠ And `ms` is the whole request: send + encode + store,
       * still not decomposed.
       *
       * ⚠⚠⚠ THIS ONE LINE IS CONCATENATED, NOT A TEMPLATE LITERAL, AND
       * THAT IS DELIBERATE — DO NOT "TIDY" IT. `anchor-sweep` reads a
       * sabotage harness's `patches: [[from, to]]` pair only when both
       * sides are BACKTICK literals, and it cannot check any anchor whose
       * text contains `${` (it would be interpolated, so the text searched
       * for is not the text written). A template literal here makes the
       * twin's case 15 anchor either INVISIBLE to the sweep or visible-
       * but-unchecked — measured, both ways, the day this was written.
       * Written in `+` the whole line is plain text, so the anchor is
       * counted AND checked, and drift is reported. ⚠ The warn line below
       * keeps its template literal: nothing anchors on it. */
      if(v.ok)console.info("instant upload: "+v.holdingId+"/"+v.slot+" — ok in "+v.ms+" ms, "+v.sentBytes+" B, "+(v.sentType||"type?")+", "+(v.sourceKind||"source?")+(v.stale?" — ⚠ STALE, answer discarded":""));
      if(v.ok||v.error==="cancelled"||v.error==="in_flight")return;
      console.warn(`instant upload: ${v.holdingId}/${v.slot} — ${v.error}: ${v.message}`);
      render();toast("1 photograph is still only on this device");
    }).catch(e=>console.error("instant upload failed",e));
  }
});
/* ══ ⚠⚠ THE PICKER HANDLER STANDS DOWN FOR A NESTED CONTROL, AND IT
 * OUTLIVED THE ONE IT WAS WRITTEN FOR — JESSE'S RULING, 2026-09-06 ═══
 *
 * It was added for the Take Photo chip: a `<button data-photocam>`
 * nested inside a frame carrying `data-photoadd`, where `closest()`
 * walks up from the chip and finds the frame, so BOTH handlers see one
 * tap and the app asks for two file dialogs at once.
 *
 * ⚠⚠ THE CHIP IS GONE AND THE BAIL STAYS. His reasoning: *"a bail that
 * protects a nested control is correct whether or not one exists
 * today."* ⚠ SO NOTHING CURRENTLY EMITS `data-photocam` AND NO CASE CAN
 * WATCH THIS LINE FAIL — it must not be read as a proven property. It
 * is a correctness statement about how this dispatcher treats nested
 * controls, kept deliberately, not a guard with live coverage.
 * ⚠⚠⚠ `main.js`'s twin was NARROWED for exactly the opposite reason —
 * there the `[data-photoadd]` half is load-bearing and covered by case
 * 3, so a dead `[data-photocam]` term beside it would have been a term
 * no case could watch fail inside a line that IS watched. */
document.addEventListener("click",e=>{if(e.target.closest("[data-photocam]"))return;const t=e.target.closest("[data-photoadd]");if(t){e.preventDefault();openEditor(t.dataset.photoadd);}});
document.addEventListener("click",e=>{const t=e.target.closest("[data-soon]");if(t){e.preventDefault();toast(t.dataset.soon);}});

// Nothing to export — this module runs for its side effects
// (it registers event listeners at load time).
